import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Play, Pause, RotateCcw, Eye, EyeOff, AlertTriangle } from 'lucide-react';

/**
 * VideoReportPlayer — replay a movement analysis with a skeleton overlay.
 *
 * Two modes (single component):
 *  - hasVideo (just-analyzed flow): renders the original <video> element
 *    underneath an absolutely-positioned <canvas> that paints the skeleton
 *    of the nearest captured frame on every requestAnimationFrame tick.
 *  - skeleton-only (saved report at /analyze/:id): no video blob to play,
 *    so we paint the skeleton onto a black canvas and tick currentTime
 *    forward using rAF. Same controls, same timeline.
 *
 * Props:
 *   videoURL: blob URL (optional)
 *   frames:   [{ t: seconds, landmarks2D: [{x,y,visibility}, ...] }, ...]
 *   duration: seconds (required for skeleton-only mode)
 *   width / height: native video resolution (for skeleton-only aspect ratio)
 *   repTimestamps: number[] — seconds at which a rep peaked
 *   problemMoments: [{t, label, severity}]  — clickable jump buttons
 */

const BONES = [
  [11, 13], [13, 15], [12, 14], [14, 16],
  [11, 12], [11, 23], [12, 24], [23, 24],
  [23, 25], [25, 27], [24, 26], [26, 28],
  [27, 31], [28, 32],
];

const fmtTime = (s) => {
  if (s == null || !isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const severityColor = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#a3a3a3',
};

const VideoReportPlayer = ({
  videoURL = null,
  frames = [],
  duration = 0,
  width = 480,
  height = 360,
  repTimestamps = [],
  problemMoments = [],
}) => {
  const hasVideo = !!videoURL;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const tickerStartRef = useRef(null);    // walltime ref when ticker started (skeleton-only)
  const tickerStartAtRef = useRef(0);     // currentTime when ticker started (skeleton-only)

  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [showVideo, setShowVideo] = useState(true);
  const [vidDims, setVidDims] = useState({ w: width, h: height });
  const [resolvedDuration, setResolvedDuration] = useState(0);

  // Use the resolved duration from the <video> element when available
  // (handles the Infinity-duration bug). Otherwise fall back to the prop
  // duration, then to the last captured frame's timestamp.
  const dur = resolvedDuration || duration || (frames.length ? frames[frames.length - 1].t : 0) || 0;

  // ── Find nearest captured frame to a given time that ACTUALLY has
  // landmarks. Some captured frames have landmarks2D=null (MediaPipe failure
  // on that single video frame). Returning a null-landmarks frame stops the
  // skeleton from rendering at that timestamp. So we walk outward from the
  // nearest neighbour until we find one with valid landmarks.
  const frameIndex = useMemo(() => frames.map(f => f.t), [frames]);
  const findFrame = useCallback((t) => {
    if (!frames.length) return null;
    // Binary search for the closest by time.
    let lo = 0, hi = frameIndex.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (frameIndex[mid] < t) lo = mid + 1;
      else hi = mid;
    }
    if (lo > 0 && Math.abs(frameIndex[lo - 1] - t) < Math.abs(frameIndex[lo] - t)) lo -= 1;
    // Walk outward until a frame with non-empty landmarks2D is found.
    const hasLm = (f) => f && Array.isArray(f.landmarks2D) && f.landmarks2D.length > 0;
    if (hasLm(frames[lo])) return frames[lo];
    let l = lo - 1, r = lo + 1;
    while (l >= 0 || r < frames.length) {
      if (l >= 0 && hasLm(frames[l])) return frames[l];
      if (r < frames.length && hasLm(frames[r])) return frames[r];
      l--; r++;
    }
    return frames[lo] || null;
  }, [frames, frameIndex]);

  // Has any captured frame got actual landmark data? Surfaces a diagnostic.
  const hasAnyLandmarks = useMemo(() => {
    for (const f of frames) {
      if (Array.isArray(f?.landmarks2D) && f.landmarks2D.length > 0) return true;
    }
    return false;
  }, [frames]);

  // ── Skeleton draw ──
  // ── Paint one full frame: video underneath, skeleton on top, all onto ONE canvas.
  // This is the bulletproof fix for the "skeleton invisible" bug. Previously the
  // skeleton was drawn on a separate <canvas> sibling above a <video>, and the
  // browser's GPU compositor frequently painted the hardware-accelerated video
  // layer ABOVE the canvas regardless of z-index. By painting the video FRAMES
  // (via ctx.drawImage(videoEl)) onto the same canvas, we eliminate layering
  // entirely — there's just one surface to render. Works on every browser.
  const drawFrame = useCallback((t) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    if (w === 0 || h === 0) return;

    // Background — black so letterboxed areas read as a film bar instead of
    // showing whatever was on the canvas previously.
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);

    // Draw the current video frame onto the canvas if requested.
    // object-contain math: scale to fit, preserve aspect, letterbox the rest.
    if (hasVideo && showVideo) {
      const v = videoRef.current;
      if (v && v.readyState >= 2 && v.videoWidth > 0 && v.videoHeight > 0) {
        const sAspect = v.videoWidth / v.videoHeight;
        const dAspect = w / h;
        let dw, dh, dx, dy;
        if (sAspect > dAspect) {
          // source wider than dest → fit by width
          dw = w;
          dh = w / sAspect;
          dx = 0;
          dy = (h - dh) / 2;
        } else {
          dh = h;
          dw = h * sAspect;
          dx = (w - dw) / 2;
          dy = 0;
        }
        try {
          ctx.drawImage(v, dx, dy, dw, dh);
        } catch (_) {
          // SecurityError on cross-origin video without CORS — ignore, skeleton still paints.
        }
      }
    }

    if (!showSkeleton) return;
    const f = findFrame(t);
    if (!f?.landmarks2D || f.landmarks2D.length === 0) return;
    const lm = f.landmarks2D;

    // Visibility filter — only skip landmarks where visibility is *explicitly*
    // tiny. MediaPipe sometimes omits the field; we trust those. Some saved
    // data has visibility uniformly low — better to draw something than nothing.
    const visible = (p) => {
      if (!p) return false;
      const vv = p.visibility;
      return vv == null || vv > 0.05;
    };

    // If none pass even this lenient filter, draw everything anyway — better
    // to show a skeleton than an empty canvas with a "Skeleton" toggle that
    // does nothing visible.
    const anyVisible = lm.some(visible);
    const isVis = anyVisible ? visible : ((p) => !!p);

    ctx.lineWidth = Math.max(2, w / 320);
    ctx.strokeStyle = '#CCFF00';
    ctx.fillStyle = '#CCFF00';
    ctx.shadowColor = 'rgba(204, 255, 0, 0.6)';
    ctx.shadowBlur = Math.max(2, w / 240);
    for (const [a, b] of BONES) {
      const A = lm[a], B = lm[b];
      if (!isVis(A) || !isVis(B)) continue;
      ctx.beginPath();
      ctx.moveTo(A.x * w, A.y * h);
      ctx.lineTo(B.x * w, B.y * h);
      ctx.stroke();
    }
    ctx.shadowBlur = 0;
    const dotR = Math.max(2.5, w / 240);
    for (let i = 0; i < lm.length; i++) {
      const p = lm[i];
      if (!isVis(p)) continue;
      ctx.beginPath();
      ctx.arc(p.x * w, p.y * h, dotR, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [findFrame, hasVideo, showVideo, showSkeleton]);

  // ── Resize canvas pixel dims to match the container's rendered size.
  // STABLE: no `currentTime` dependency. This used to recreate on every tick,
  // which caused the listener-registration effect below to tear down and
  // re-register on every render — re-firing `loadedmetadata` and snapping the
  // playhead back to 0 on every frame (the "shaking" bug).
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const cont = containerRef.current;
    if (!canvas || !cont) return;
    const rect = cont.getBoundingClientRect();
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    canvas.width = Math.round(rect.width);
    canvas.height = Math.round(rect.height);
  }, []);

  // Observe window + container resizes. Trigger one redraw at the latest
  // currentTime (read via ref so this effect doesn't re-fire each tick).
  const currentTimeRef = useRef(0);
  useEffect(() => { currentTimeRef.current = currentTime; }, [currentTime]);

  useEffect(() => {
    const apply = () => {
      resizeCanvas();
      drawFrame(currentTimeRef.current);
    };
    apply();
    window.addEventListener('resize', apply);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      ro = new ResizeObserver(apply);
      ro.observe(containerRef.current);
    }
    return () => {
      window.removeEventListener('resize', apply);
      if (ro) ro.disconnect();
    };
  }, [resizeCanvas, drawFrame]);

  // ── Drive currentTime from the <video> element.
  // Registers listeners ONCE per (hasVideo, videoURL) pair. Critically does
  // NOT depend on `resizeCanvas` or `currentTime`, so the effect no longer
  // tears down on every frame. `onMeta` is guarded by a ref so it runs at
  // most once per video source — preventing the "snap playhead to 0 every
  // tick" loop that produced the visual shake.
  const metaInitDoneRef = useRef(false);
  useEffect(() => {
    if (!hasVideo) return;
    const v = videoRef.current;
    if (!v) return;
    // Reset the one-shot guard whenever a new source is mounted.
    metaInitDoneRef.current = false;

    const onTime = () => setCurrentTime(v.currentTime);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    const onMeta = () => {
      if (metaInitDoneRef.current) return;
      metaInitDoneRef.current = true;
      setVidDims({ w: v.videoWidth || width, h: v.videoHeight || height });
      // WebKit/Chrome bug: some blob videos report duration === Infinity
      // until they've been seeked past the end. Force-resolve it so the
      // timeline + seek logic have a real number to work with.
      if (!isFinite(v.duration) || v.duration <= 0) {
        const onSeekEnd = () => {
          v.removeEventListener('seeked', onSeekEnd);
          // Rewind ONCE after the probe, so playback starts from t=0.
          try { v.currentTime = 0; } catch (_) { /* ignore */ }
          setResolvedDuration(isFinite(v.duration) && v.duration > 0 ? v.duration : 0);
        };
        v.addEventListener('seeked', onSeekEnd);
        try { v.currentTime = 1e7; } catch (_) {
          v.removeEventListener('seeked', onSeekEnd);
        }
      } else {
        setResolvedDuration(v.duration);
      }
    };

    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('ended', onPause);
    if (v.readyState >= 1) onMeta();
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('ended', onPause);
    };
  }, [hasVideo, videoURL, width, height]);

  // ── Tick currentTime forward when in skeleton-only mode and playing ──
  useEffect(() => {
    if (hasVideo) return;
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }
    tickerStartRef.current = performance.now();
    tickerStartAtRef.current = currentTime >= dur ? 0 : currentTime;
    if (currentTime >= dur) setCurrentTime(0);
    const tick = () => {
      const elapsed = (performance.now() - tickerStartRef.current) / 1000;
      const t = tickerStartAtRef.current + elapsed;
      if (t >= dur) {
        setCurrentTime(dur);
        setPlaying(false);
        return;
      }
      setCurrentTime(t);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, hasVideo, dur]);

  // ── Redraw whenever currentTime or toggles change ──
  useEffect(() => { drawFrame(currentTime); }, [currentTime, drawFrame]);

  // ── While the <video> is playing, drive a rAF paint loop so the video
  // frames on the canvas update at display refresh rate (timeupdate fires
  // only ~4-15 Hz which would look choppy). When paused/skeleton-only we
  // rely on the currentTime-change redraw above. ──
  const paintLoopRef = useRef(null);
  useEffect(() => {
    if (!hasVideo) return undefined;
    if (!playing) return undefined;
    const loop = () => {
      const v = videoRef.current;
      drawFrame(v ? v.currentTime : currentTimeRef.current);
      paintLoopRef.current = requestAnimationFrame(loop);
    };
    paintLoopRef.current = requestAnimationFrame(loop);
    return () => {
      if (paintLoopRef.current) cancelAnimationFrame(paintLoopRef.current);
      paintLoopRef.current = null;
    };
  }, [playing, hasVideo, drawFrame]);

  // ── Controls ──
  const togglePlay = () => {
    if (hasVideo) {
      const v = videoRef.current;
      if (!v) return;
      if (v.paused) v.play(); else v.pause();
    } else {
      setPlaying(p => !p);
    }
  };

  const seek = (t) => {
    const clamped = Math.max(0, Math.min(dur, t));
    if (hasVideo && videoRef.current) {
      videoRef.current.currentTime = clamped;
    } else {
      setCurrentTime(clamped);
      if (playing) {
        // restart ticker base so it continues from new position
        tickerStartRef.current = performance.now();
        tickerStartAtRef.current = clamped;
      }
    }
  };

  const restart = () => seek(0);

  // ── Aspect ratio for skeleton-only canvas ──
  const aspect = vidDims.w && vidDims.h ? vidDims.w / vidDims.h : (width / height || 16 / 9);

  return (
    <div className="w-full">
      <div
        ref={containerRef}
        className="relative bg-black rounded-sm overflow-hidden mx-auto"
        style={{ aspectRatio: aspect, maxHeight: '60vh' }}
        data-testid="report-player"
      >
        {hasVideo && (
          <video
            ref={videoRef}
            src={videoURL}
            playsInline
            // The video element is invisible — its only job is to decode frames
            // so the canvas can paint them via ctx.drawImage. We don't show it
            // directly because hardware-composited <video> layers fight z-index
            // and frequently paint above sibling <canvas> elements on Safari.
            // Same-origin (backend behind same ingress) so no crossOrigin attr needed.
            className="absolute inset-0 w-full h-full object-contain opacity-0 pointer-events-none"
          />
        )}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full pointer-events-none"
        />
        {/* Surface a clear diagnostic if this saved analysis has NO landmark
            data — that way users (and us) immediately know why the skeleton
            toggle does nothing, instead of silently rendering an empty canvas. */}
        {!hasAnyLandmarks && (
          <div
            className="absolute top-2 left-2 z-10 px-2.5 py-1 rounded-sm bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] uppercase tracking-wider font-secondary"
            data-testid="report-player-no-landmarks"
          >
            No skeleton data saved
          </div>
        )}
      </div>

      {/* Controls + timeline */}
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2">
          <button
            onClick={togglePlay}
            className="w-9 h-9 rounded-sm bg-primary text-black hover:bg-primary/90 flex items-center justify-center transition-colors"
            data-testid="report-player-play"
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={restart}
            className="w-9 h-9 rounded-sm bg-zinc-900 hover:bg-zinc-800 text-zinc-300 flex items-center justify-center transition-colors"
            data-testid="report-player-restart"
            aria-label="Restart"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <div className="flex-1 text-xs text-zinc-400 font-secondary tracking-wider">
            {fmtTime(currentTime)} <span className="text-zinc-700">/</span> {fmtTime(dur)}
          </div>
          {hasVideo && (
            <button
              onClick={() => setShowVideo(s => !s)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-zinc-900 hover:bg-zinc-800 text-xs text-zinc-300 transition-colors"
              data-testid="report-player-toggle-video"
              title="Show or hide the original video underneath the skeleton"
            >
              {showVideo ? <Eye className="w-3.5 h-3.5 text-primary" /> : <EyeOff className="w-3.5 h-3.5" />}
              Video
            </button>
          )}
          <button
            onClick={() => setShowSkeleton(s => !s)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-zinc-900 hover:bg-zinc-800 text-xs text-zinc-300 transition-colors"
            data-testid="report-player-toggle-skeleton"
            title="Show or hide the green skeleton wireframe"
          >
            {showSkeleton ? <Eye className="w-3.5 h-3.5 text-primary" /> : <EyeOff className="w-3.5 h-3.5" />}
            Skeleton
          </button>
        </div>

        {/* Timeline */}
        <div
          className="relative h-8 bg-zinc-900 rounded-sm cursor-pointer select-none"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const pct = (e.clientX - rect.left) / rect.width;
            seek(pct * dur);
          }}
          data-testid="report-player-timeline"
        >
          {/* progress bar */}
          <div
            className="absolute inset-y-0 left-0 bg-primary/15 rounded-sm pointer-events-none"
            style={{ width: dur ? `${(currentTime / dur) * 100}%` : '0%' }}
          />
          {/* rep markers */}
          {repTimestamps.map((t, i) => (
            <div
              key={`rep-${i}`}
              className="absolute top-0 bottom-0 w-px bg-primary/80 pointer-events-none"
              style={{ left: dur ? `${(t / dur) * 100}%` : 0 }}
              title={`Rep ${i + 1} · ${fmtTime(t)}`}
            />
          ))}
          {/* problem moment markers */}
          {problemMoments.map((p, i) => (
            <div
              key={`pm-${i}`}
              className="absolute top-0 bottom-0 w-0.5 pointer-events-none"
              style={{
                left: dur ? `${(p.t / dur) * 100}%` : 0,
                background: severityColor[p.severity] || severityColor.medium,
              }}
              title={`${p.label} · ${fmtTime(p.t)}`}
            />
          ))}
          {/* playhead */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none shadow-[0_0_4px_rgba(255,255,255,0.6)]"
            style={{ left: dur ? `${(currentTime / dur) * 100}%` : 0 }}
          />
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-wider text-zinc-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-primary inline-block" /> Rep</span>
          {problemMoments.length > 0 && (
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 inline-block" /> Problem moment</span>
          )}
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-white inline-block" /> Playhead</span>
        </div>
      </div>

      {/* Problem moment jump list */}
      {problemMoments.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest text-zinc-500">
            <AlertTriangle className="w-3 h-3" /> Problem moments — click to jump
          </div>
          <div className="flex flex-wrap gap-2">
            {problemMoments.map((p, i) => (
              <button
                key={i}
                onClick={() => { seek(p.t); }}
                className="text-xs px-2.5 py-1.5 rounded-sm border border-zinc-800 hover:border-amber-500 bg-zinc-950 hover:bg-amber-500/10 transition-colors flex items-center gap-1.5"
                data-testid={`report-problem-${i}`}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: severityColor[p.severity] || severityColor.medium }}
                />
                <span className="text-zinc-200">{p.label}</span>
                <span className="text-zinc-500 font-secondary tracking-wider">{fmtTime(p.t)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoReportPlayer;
